/**
 * Technology Selection State Management Hook
 *
 * Manages row selection state for the technology table using KEY-BASED selection.
 * Unlike index-based selection, this approach survives:
 * - Pagination changes
 * - Filter changes
 * - Data reordering
 * - Async updates
 *
 * Key features:
 * - Selected technology keys (not indices)
 * - Computed selected technologies from keys with validation
 * - Automatic clearing when filters change
 *
 * Risk Level: Low
 * - Has one useEffect for clearing selection on filter change
 * - Uses proper React dependencies for optimal performance
 * - Computes selectedTechnologies from keys with validation
 */

import { isEqual } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { TechnologySelectionReturn } from '@/sections/technology/types/table.types';
import type { Technology } from '@/types';

/**
 * Parameters for technology filters
 * Used to detect filter changes and clear selection
 * Flexible to accommodate various filter structures
 */
type Filters = Record<string, unknown>;

/**
 * Custom hook for managing technology table row selection
 *
 * Uses KEY-BASED selection (not index-based) to survive:
 * - Pagination changes
 * - Filter changes
 * - Data reordering
 *
 * Example usage:
 * ```tsx
 * const { selectedKeys, selectedTechnologies, toggleSelection } = useTechnologySelection(
 *   technologies,
 *   filters
 * );
 *
 * <Table
 *   selection={{ value: selectedKeys, onChange: (keys) => keys.forEach(toggleSelection) }}
 * />
 * ```
 *
 * @param technologies - Array of all technologies in the current view
 * @param filters - Current filter state (used to detect changes and clear selection)
 * @returns Selection state and control functions
 */
export const useTechnologySelection = (
  technologies: Technology[],
  filters: Filters
): TechnologySelectionReturn => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const filtersRef = useRef(filters);

  // Compute selected technologies from keys
  // Uses key-based lookup for reliability across pagination and filtering
  const { isRowsSelected, selectedTechnologies } = useMemo(() => {
    const updatedSelectedTechnologies = selectedKeys
      .map(key => {
        const technology = technologies.find(t => t.key === key);

        if (!technology) {
          // Technology no longer in current view (filtered out, deleted, etc.)
          // This is expected behavior and not an error
          return null;
        }

        return technology;
      })
      .filter((technology): technology is Technology => technology !== null);

    return {
      isRowsSelected: selectedKeys.length > 0,
      selectedTechnologies: updatedSelectedTechnologies,
    };
  }, [selectedKeys, technologies]);

  // Clear selection when filters change
  // Uses deep comparison to avoid unnecessary clears
  useEffect(() => {
    if (!isEqual(filtersRef.current, filters)) {
      setSelectedKeys([]);
      filtersRef.current = filters;
    }
  }, [filters]);

  // Toggle selection for a single technology
  const toggleSelection = (key: string) => {
    setSelectedKeys(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedKeys([]);
  };

  // Select all technologies in current view
  const selectAll = () => {
    setSelectedKeys(technologies.map(tech => tech.key));
  };

  return {
    selectedKeys,
    setSelectedKeys,
    selectedTechnologies,
    isRowsSelected,
    toggleSelection,
    clearSelection,
    selectAll,
  };
};
